import { Map, MapBrowserEvent, Overlay } from 'ol';
import {
  useCallback,
  useContext, useEffect, useRef, useState,
} from 'react';
import { Coordinate } from 'ol/coordinate';
import { Layer } from 'ol/layer';
import { availableProjections } from '../../../configs/projections';
import MainAppContext from '../../MainAppContext';
import ProductsContext from '../../ProductsContext';
import TileViewContext from '../../TileViewContext';
import { generatePopupInfo, resetPopup } from './utils';
import TimeSeriesContext from '../../TimeSeriesContext';
import LayerViewContext from '../../LayerViewContext';
import { PopupLayerEntry } from './providers/types';

type GetLayerTooltipFn = (
  layer: Layer,
  coordinate: Coordinate | number[],
  destinationProjection: string,
  map: Map,
) => Promise<PopupLayerEntry[]>;

function usePopup(
  map: Map,
  index: number,
  getLayerTooltip: GetLayerTooltipFn,
  playbackTooltipEnabled = false,
) {
  const popupRef = useRef<HTMLDivElement | null>(null);
  const [popupInfo, setPopupInfo] = useState<Record<string, unknown>>({});
  // Keep a ref to always call the latest getLayerTooltip (avoids stale closure)
  const getLayerTooltipRef = useRef(getLayerTooltip);
  useEffect(() => { getLayerTooltipRef.current = getLayerTooltip; }, [getLayerTooltip]);
  const { onHoverEvent: tileOnHoverEvent } = useContext(TileViewContext);
  const { onHoverEvent: layerOnHoverEvent } = useContext(LayerViewContext);
  const { activeProducts } = useContext(ProductsContext);
  const { playbackMode, realTimeMode, currentProjection } = useContext(MainAppContext);
  const { currentPosition } = useContext(TimeSeriesContext);
  const popupAppearanceTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const popupDisappearanceTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const updatePopup = useCallback((
    popup : Overlay,
    event: MapBrowserEvent<PointerEvent>,
  ) => {
    if (!playbackMode && !realTimeMode) {
      map.removeOverlay(popup);
    }
    if (popupAppearanceTimer.current) {
      clearTimeout(popupAppearanceTimer?.current);
    }

    popupAppearanceTimer.current = setTimeout(async () => {
      const info = await generatePopupInfo({
        layers: map.getAllLayers(),
        coordinate: event ? event.coordinate : popup.getPosition() as Coordinate,
        popup,
        destinationProjection: availableProjections.find(
          (projection) => projection.name === currentProjection,
        )?.epsg as string,
        map,
        pixel: event ? event.pixel : [0, 0],
        getLayerTooltip: getLayerTooltipRef.current,
      });

      if (Object.keys(info).length) {
        setPopupInfo(info);
        const alreadyVisible = !!map.getOverlayById('popup') && popup.getElement()?.classList.contains('show');
        if (alreadyVisible) {
          // Popup already on screen — just update content, don't touch the overlay or classes
          return;
        }
        if (!playbackMode && !realTimeMode) {
          popup.getElement()?.classList.add('hide');
          popup.getElement()?.classList.remove('show');
        }
        map.addOverlay(popup);
        setTimeout(() => {
          if (!playbackMode || playbackTooltipEnabled) {
            popup.getElement()?.classList.add('show');
          }
        }, 50);
        if (!playbackMode && !realTimeMode) {
          popup.getElement()?.classList.remove('hide');
        }
      }
    }, playbackMode ? 100 : 1000);

    if (popupDisappearanceTimer.current) {
      clearTimeout(popupDisappearanceTimer?.current);
    }

    popupDisappearanceTimer.current = setTimeout(() => {
      if (!playbackMode && !realTimeMode) {
        popup.getElement()?.classList.add('hide');
        popup.getElement()?.classList.remove('show');
        setTimeout(() => {
          map?.removeOverlay(popup);
        }, 300);
      }
    }, 60000);
  }, [currentProjection, map, playbackMode, playbackTooltipEnabled, realTimeMode]);

  useEffect(() => {
    const createdPopup = map?.getOverlayById('popup');
    if (popupRef.current && !createdPopup) {
      // Create popup
      const popup = new Overlay({
        id: 'popup',
        element: popupRef.current as HTMLDivElement,
      });
      map?.addOverlay(popup);
    }
  }, [map]);

  useEffect(() => {
    let popup = map?.getOverlayById('popup');

    if (!popup) {
      popup = new Overlay({
        id: 'popup',
        element: popupRef.current as HTMLDivElement,
      });
    }
    const removePopup = (event: MouseEvent) => {
      if ((event.target as Element)?.tagName !== 'CANVAS') {
        if (popup && map) {
          map?.removeOverlay(popup);
          if (popupAppearanceTimer?.current) { clearTimeout(popupAppearanceTimer?.current); }
        }
      }
    };

    // Named so the cleanup can remove it: this effect re-runs on every mode/projection change
    // and an anonymous listener would pile up (each with its own stale closure).
    const onPointerMove = (event: MapBrowserEvent<PointerEvent>) => {
      map?.removeOverlay(popup as Overlay);
      updatePopup(popup as Overlay, event);
    };

    if (popup && map) {
      // Add event listener to capture onhover
      map.on('pointermove', onPointerMove);

      // Add global hover listener to disabled
      // popup when cursor is over a element that is not a map (canvas)
      window.addEventListener('mouseover', removePopup);
    }
    return () => {
      map?.un('pointermove', onPointerMove);
      if (popupDisappearanceTimer.current) {
        clearTimeout(popupDisappearanceTimer.current);
      }
      if (popupAppearanceTimer.current) {
        clearTimeout(popupAppearanceTimer.current);
      }

      window.removeEventListener('mouseover', removePopup);
    };
  }, [map, playbackMode, updatePopup, realTimeMode]);

  useEffect(() => {
    const popup = map?.getOverlayById('popup');
    if ((playbackMode || realTimeMode)
      && currentPosition
      && popup) {
      if (tileOnHoverEvent?.event) {
        updatePopup(popup, tileOnHoverEvent?.event);
      }
      if (layerOnHoverEvent?.event) {
        updatePopup(popup, layerOnHoverEvent?.event);
      }
    }
  }, [
    currentPosition,
    layerOnHoverEvent?.event,
    map,
    playbackMode,
    tileOnHoverEvent?.event,
    realTimeMode,
    updatePopup]);

  useEffect(() => {
    if (map && activeProducts) {
      resetPopup(map, setPopupInfo);
    }
  }, [activeProducts, map]);

  return {
    popupContainer: popupRef,
    setPopupInfo,
    popupInfo,
  };
}

export default usePopup;
